import { COLOR_THEMES } from '@/data/color-themes';
import {
  BillLabel,
  BillPaymentLabels,
  BillPaymentMethods,
  BillStatus,
  messageToFounderTitles,
  TransactionStatus,
  TransactionToBillStatus,
} from '@/data/payments';
import * as dateFns from 'date-fns';

export const getBillStatusLabel = (
  status: BillStatus | TransactionStatus,
  type = 'bill',
): string => {
  const normalizedStatus =
    type === 'bill'
      ? status
      : TransactionToBillStatus[status as TransactionStatus];
  return BillLabel[normalizedStatus as BillStatus];
};

export const getBillStatusTheme = (status: BillStatus): string => {
  const { SUCCESS, PENDING, IN_TRANSIT, CANCELED, FAILED, SETTLED, SUBMITTED } =
    TransactionStatus;
  const {
    UNREAD,
    PROCESSING,
    APPROVING_VENDOR,
    WAITING_ON_FOUNDER,
    WAITING_ON_CLEARCO,
    COMPLETED,
    ERROR,
    CANCELLED,
    SCHEDULED,
    PAYING_VENDOR,
    WAITING_FOR_PAYMENT,
    BNPL_APPROVED,
    BNPL_DECLINED,
    AUTO_DEBITING_ACTIVE,
  } = BillStatus;
  if ([SUCCESS, COMPLETED, BNPL_APPROVED, SETTLED].includes(status)) {
    return COLOR_THEMES.SUCCESS;
  } else if (
    [
      IN_TRANSIT,
      PENDING,
      SUBMITTED,
      PAYING_VENDOR,
      SCHEDULED,
      UNREAD,
      PROCESSING,
      APPROVING_VENDOR,
      WAITING_ON_FOUNDER,
      WAITING_ON_CLEARCO,
      WAITING_FOR_PAYMENT,
      AUTO_DEBITING_ACTIVE,
    ].includes(status)
  ) {
    return COLOR_THEMES.INFO;
  } else if (
    [CANCELED, FAILED, ERROR, CANCELLED, BNPL_DECLINED].includes(status)
  ) {
    return COLOR_THEMES.DANGER;
  }

  return COLOR_THEMES.WARNING;
};

export const getBillPaymentMethod = (
  paymentMethod: BillPaymentMethods,
): string => {
  return BillPaymentLabels[paymentMethod];
};

export const founderBillMessage = (
  status: BillStatus,
):
  | {}
  | {
      title: messageToFounderTitles;
      bannerType: 'success' | 'error' | 'warn';
    } => {
  let messageToFounder = {};
  if (status === BillStatus.ERROR) {
    messageToFounder = {
      title: messageToFounderTitles.error,
      bannerType: 'error',
    };
  } else if (status === BillStatus.COMPLETED) {
    messageToFounder = {
      title: messageToFounderTitles.completed,
      bannerType: 'success',
    };
  } else if (status === BillStatus.WAITING_ON_FOUNDER) {
    messageToFounder = {
      title: messageToFounderTitles.waiting_on_founder,
      bannerType: 'warn',
    };
  }

  return messageToFounder;
};

export const getShortBillId = (id?: string): string =>
  id ? id.slice(-5).toUpperCase() : '---';

export const getBillFees = (paymentMethod: BillPaymentMethods): string => {
  if (paymentMethod === BillPaymentMethods.CREDIT_CARD) {
    return '2.9%';
  }
  return 'Free';
};

export const dueDateToEasternDateTime = (dueDate?: string): string => {
  // being a no timezone date if it gets parsed to date will be parsed to UTC
  // just cropping the string displays the right date

  return dueDate ? dueDate.substring(0, 10) : '';
};

export const getFormattedDateString = (date?: string): string => {
  if (!date) {
    return '';
  }
  const dateUtc = new Date(date)
    .toISOString()
    .replace('T', ' ')
    .substring(0, 19);
  return dateFns.format(dateFns.parseISO(dateUtc), 'MMM dd, yyyy');
};

export const limitFileName = (str: string): string => {
  const max = 25;
  return str.length > max ? `${str.substring(0, max)}...` : str;
};

export const userChangedTheForm = (initialValues: any, formOutput: any) => {
  return JSON.stringify(initialValues) !== JSON.stringify(formOutput);
};

export const validEmailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
